import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { skeleton } from '../../components/skeleton.js';
import { toast } from '../../components/toast.js';
import { assembleCatalogue, liveCount } from './catalogue.js';
import { renderDepth, renderLiveGrid } from './live.js';
import { fillGrid, renderCatalogue } from './grid.js';
import { renderHelp } from './help.js';
import { cxBtn, sectionError } from './ui.js';
import { openConnectorDialog } from './dialog.js';

function query(productId) {
  return productId ? `?product_id=${encodeURIComponent(productId)}` : '';
}

function isAdmin(opts) {
  return !!(opts.me && opts.me.role === 'admin');
}

function mcpNote(result) {
  if (!result || !result.error) return '';
  if (result.status === 403) return 'MCP servers are listed for workspace admins.';
  return '';
}

async function loadBundle(productId) {
  const q = query(productId);
  const [integrations, providers, depth, mcp, session] = await Promise.all([
    productId ? api(`/connectors${q}`) : Promise.resolve({ integrations: [] }),
    api('/connectors/providers'),
    productId ? api(`/connectors/depth${q}`) : Promise.resolve({ analysis_depth: 0, depth_breakdown: {} }),
    productId ? api(`/mcp/connect${q}`) : Promise.resolve({ servers: [] }),
    api('/support/session'),
  ]);
  return { integrations, providers, depth, mcp, session };
}

function header(state, onRecheck, onAdd) {
  const n = liveCount(state.rows);
  return el('header', { class: 'cx-head rv' },
    el('div', { class: 'cx-head-copy' },
      el('h1', { class: 'cx-title' }, `${n} live. `, el('em', {}, 'Checked when you open this page.')),
      el('p', {}, 'Every source is re-checked when you open this page. Synced sources show their last sync.')),
    el('div', { class: 'cx-head-actions' },
      cxBtn(state.checking ? 'Re-checking…' : 'Re-check All', {
        className: 'cx-btn cx-btn-lg',
        disabled: state.checking,
        onClick: onRecheck,
      }),
      cxBtn('Add Connector', { className: 'cx-cta cx-btn-lg', arrow: true, onClick: onAdd })),
    el('p', { class: 'cx-sr', 'aria-live': 'polite' }, state.liveMsg || ''));
}

function paint(root, state, handlers) {
  clear(root);
  if (!state.opts.productId) {
    root.append(sectionError('Pick a product', 'Connectors belong to a product. Choose one in the top bar.', null));
    return;
  }
  if (state.fatal) {
    root.append(sectionError('Connectors unavailable', state.fatal, handlers.reload));
    return;
  }
  root.append(
    header(state, handlers.recheck, handlers.add),
    el('div', { class: 'cx-live' },
      renderDepth(state.depth, state.depthError, handlers.reload),
      renderLiveGrid(state.rows, { admin: isAdmin(state.opts) })),
    renderCatalogue(state, handlers.tab, handlers.query, handlers.action, handlers.reload),
    renderHelp(state.opts, state.session),
    el('p', { class: 'cx-foot' }, '✦ Any degraded connector is named inside the answer it affects — so nobody reads a stale number as current.'),
  );
}

function applyBundle(state, bundle) {
  const integrations = bundle.integrations || {};
  const providers = bundle.providers || {};
  if (integrations.error && state.opts.productId) {
    state.fatal = integrations.error;
    return;
  }
  state.fatal = '';
  state.rows = integrations.integrations || [];
  state.depth = bundle.depth && !bundle.depth.error ? bundle.depth : { analysis_depth: 0, depth_breakdown: {} };
  state.depthError = bundle.depth && bundle.depth.error ? bundle.depth.error : '';
  state.providersError = providers.error || '';
  const servers = bundle.mcp && !bundle.mcp.error ? (bundle.mcp.servers || []) : [];
  state.items = state.providersError ? [] : assembleCatalogue(providers.providers || [], state.rows, servers);
  state.mcpNote = state.providersError ? '' : mcpNote(bundle.mcp);
  if (bundle.mcp && bundle.mcp.error && bundle.mcp.status !== 403) state.mcpNote = 'MCP servers could not be loaded.';
  const session = bundle.session && bundle.session.session;
  state.session = session || null;
}

async function recheckAll(state, root, handlers) {
  const rows = state.rows || [];
  if (!rows.length) { toast('Nothing to re-check'); return; }
  state.checking = true;
  state.liveMsg = `Re-checking ${rows.length} source${rows.length === 1 ? '' : 's'}.`;
  paint(root, state, handlers);
  let blocked = '';
  let failed = 0;
  await Promise.all(rows.map(async (row) => {
    const result = await api(`/connectors/${encodeURIComponent(row.id)}/test`, { method: 'POST' });
    if (!result || result.error) {
      if (result && result.status === 403) blocked = result.error || 'Only admins can re-check connectors.';
      else failed += 1;
      return;
    }
    row.latency_ms = result.latency_ms;
    if (result.status) row.health_status = result.status;
    row.last_tested_at = new Date().toISOString();
    if (result.tool_count != null) row.tool_count = result.tool_count;
  }));
  if (state.dead) return;
  const depth = await api(`/connectors/depth${query(state.opts.productId)}`);
  if (depth && !depth.error) state.depth = depth;
  state.checking = false;
  state.liveMsg = blocked || (failed
    ? `Re-check finished. ${failed} could not be reached.`
    : 'Re-check finished.');
  paint(root, state, handlers);
  toast(state.liveMsg, blocked || failed ? 'error' : 'info');
}

function scrollToCatalogue(root) {
  const node = root.querySelector('#cx-catalogue');
  if (!node) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  node.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
}

function onTab(root, state, handlers, id) {
  state.tab = id;
  root.querySelectorAll('.cx-tab').forEach((node) => {
    const on = node.getAttribute('data-tab') === id;
    node.classList.toggle('is-on', on);
    node.setAttribute('aria-pressed', on ? 'true' : 'false');
  });
  const grid = root.querySelector('.cx-grid');
  if (grid) fillGrid(grid, state.items, state, handlers.action);
}

function onQuery(root, state, handlers, event) {
  state.query = event.target.value;
  const grid = root.querySelector('.cx-grid');
  if (grid) fillGrid(grid, state.items, state, handlers.action);
}

export function startConnect(item, mode, opts, reload) {
  openConnectorDialog(item, mode, opts, reload);
}

export function renderConnectors(opts) {
  const root = el('div', { class: 'cx' });
  const state = {
    opts,
    rows: [],
    items: [],
    depth: null,
    tab: 'all',
    query: '',
    checking: false,
    fatal: '',
    dead: false,
    auto: false,
  };
  const handlers = {
    reload: () => load(true),
    recheck: () => recheckAll(state, root, handlers),
    add: () => scrollToCatalogue(root),
    tab: (id) => onTab(root, state, handlers, id),
    query: (event) => onQuery(root, state, handlers, event),
    action: (item, mode) => {
      if (item.soon) return;
      if (!isAdmin(opts)) {
        toast('Only admins can view or edit credentials.', 'error');
        return;
      }
      startConnect(item, mode, opts, () => load(false));
    },
  };
  root.append(skeleton(6));
  root.addEventListener('bonnie:unmount', () => { state.dead = true; });
  load(false);
  return root;

  async function load(manual) {
    const bundle = await loadBundle(opts.productId);
    if (state.dead) return;
    applyBundle(state, bundle);
    paint(root, state, handlers);
    if (!manual && !state.auto && state.rows.length && !state.fatal) {
      state.auto = true;
      recheckAll(state, root, handlers);
    }
  }
}
